import { ERA_IDS, ERAS, type EraId } from '../config/eras';
import { carChange } from './calendar';
import { Rng } from './prng';

/**
 * Which era races on a date. Every car comes round once every nine days, in a
 * new order each time, and never returns within three days. From
 * ROTATION_2_FROM the classics (the cars from before 1970) are kept apart:
 * two classics never race on consecutive days, and there are never three
 * modern cars in a row, so a classic comes round every two or three days; and
 * a classic only returns once the other three have raced (about every nine
 * days). Modern cars may follow each other and come back sooner. Earlier days
 * keep the cars they had (their boards and replays depend on them).
 */
const DAY_MS = 86400000;

function dayNumber(date: string): number {
  return Math.floor(Date.parse(date + 'T12:00:00Z') / DAY_MS);
}

function dateOf(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

function blockOrder<T>(items: readonly T[], key: string, block: number): T[] {
  const order: T[] = [...items];
  const rng = Rng.fromString(key + '|' + block);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    const t = order[i];
    order[i] = order[j];
    order[j] = t;
  }
  return order;
}

/**
 * A seeded shuffle of `items` per cycle (one day each), so each comes round
 * once a cycle, in a new order each time, and never returns within
 * `spreadShare` of a cycle.
 */
export function cycleForDate<T>(items: readonly T[], key: string, date: string, spreadShare = 1 / 3): T {
  const n = items.length;
  const d = dayNumber(date);
  const block = Math.floor(d / n);
  const order = blockOrder(items, key, block);
  // The last few of the previous cycle can't open this one, so nothing comes
  // back within `spread` days. Swaps only touch the first n - spread slots, so
  // every cycle's tail is its raw shuffle and nothing chains back.
  const spread = Math.floor(n * spreadShare);
  const recent = blockOrder(items, key, block - 1).slice(n - spread);
  for (let i = 0; i < spread; i++) {
    if (!recent.includes(order[i])) continue;
    for (let j = spread; j < n - spread; j++) {
      if (!recent.includes(order[j])) {
        const t = order[i];
        order[i] = order[j];
        order[j] = t;
        break;
      }
    }
  }
  return order[d - block * n];
}

/** The day the rotation started keeping the classics apart. */
export const ROTATION_2_FROM = '2026-10-01';

/** The cars from before 1970: 1932, 1937, 1954 and 1961 (four of the nine, so they can always be kept apart). */
export function isClassic(era: EraId): boolean {
  return ERAS[era].year < 1970;
}

export function eraForDate(date: string): EraId {
  return carChange(date) ?? rotationEra(date);
}

/** The rotation's car for a date, before any one-off change to the calendar (the next days follow on from it). */
export function rotationEra(date: string): EraId {
  if (date < ROTATION_2_FROM) return cycleForDate(ERA_IDS, 'eras', date);
  const n = ERA_IDS.length;
  const i = dayNumber(date) - dayNumber(ROTATION_2_FROM);
  const block = Math.floor(i / n);
  return rotationBlock(block)[i - block * n];
}

/** The nine-day blocks from ROTATION_2_FROM, built in order (each follows on from the last). */
const blocks: EraId[][] = [];

function rotationBlock(block: number): EraId[] {
  for (let b = blocks.length; b <= block; b++) {
    // The nine days before the block: the last block, or for the first, the old rotation's last days.
    const n = ERA_IDS.length;
    const start = dayNumber(ROTATION_2_FROM) + b * n;
    const before = b === 0 ? Array.from({ length: n }, (_, k) => rotationEra(dateOf(start - n + k))) : blocks[b - 1];
    blocks.push(arrange(before, Rng.fromString('eras2|' + b)));
  }
  return blocks[block];
}

/**
 * A block's order: a seeded shuffle of every car, changed only as much as it
 * takes (trying the cars in shuffled order, day by day, backing up when stuck)
 * so that no car returns within three days, no classic follows a classic or
 * returns before the other three have raced, and no third modern car follows two.
 */
function arrange(before: EraId[], rng: Rng): EraId[] {
  const pool: EraId[] = [...ERA_IDS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const days = [...before];
  const fits = (e: EraId): boolean => {
    const last = days.length - 1;
    if (days.slice(-3).includes(e)) return false;
    if (isClassic(e)) return !isClassic(days[last]) && !days.filter(isClassic).slice(-3).includes(e);
    return isClassic(days[last]) || isClassic(days[last - 1]);
  };
  const place = (): boolean => {
    if (days.length === before.length + pool.length) return true;
    for (const e of pool) {
      if (days.includes(e, before.length) || !fits(e)) continue;
      days.push(e);
      if (place()) return true;
      days.pop();
    }
    return false;
  };
  // There's always a way (the tests check decades of days); the plain shuffle is only a fallback.
  return place() ? days.slice(before.length) : pool;
}

/**
 * Like cycleForDate, but spaced out: each cycle is a seeded shuffle built one
 * day at a time, never picking an item seen fewer than `minGap` days ago (if
 * it can help it). Cycles are built in order from the one holding `epoch`, so
 * every date gets the same answer.
 */
export function spacedCycleForDate<T>(items: readonly T[], key: string, date: string, minGap: number, epoch: string): T {
  const n = items.length;
  const d = dayNumber(date);
  const block = Math.floor(d / n);
  const first = Math.floor(dayNumber(epoch) / n);
  let prev: T[] = blockOrder(items, key, Math.min(block, first));
  for (let b = first + 1; b <= block; b++) {
    const rng = Rng.fromString(key + '|spaced|' + b);
    const left = [...items];
    const order: T[] = [];
    for (let i = 0; i < n; i++) {
      // Days since each remaining item last came up (it was in the previous cycle).
      const gap = (x: T): number => i + n - prev.indexOf(x);
      const ok = left.filter((x) => gap(x) >= minGap);
      const pool = ok.length ? ok : [left.reduce((a, x) => (gap(x) > gap(a) ? x : a))];
      const pickd = pool[rng.int(0, pool.length - 1)];
      order.push(pickd);
      left.splice(left.indexOf(pickd), 1);
    }
    prev = order;
  }
  return prev[d - block * n];
}
